import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Token is kept in memory only — never in localStorage.
  // Persistence is handled by the httpOnly session_token cookie.
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGate, setShowGate] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      try {
        // The httpOnly cookie (session_token) is sent automatically.
        // No need to attach a Bearer header here.
        const response = await api.get('/auth/me');
        if (!cancelled) {
          setUser(response.data);
          setShowGate(false);
        }
      } catch (err) {
        const status = err?.response?.status;
        // 401 is expected for unauthenticated visitors — don't log noise.
        if (status && status !== 401) {
          console.warn('Auth session lookup failed:', status);
        }
        // Cookie missing / expired / backend unreachable.
        // Fall back to cached user_data (non-sensitive display data) so
        // users don't see the gate flicker when the backend is slow.
        const cached = localStorage.getItem('user_data');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (!cancelled) {
              setUser(parsed);
              setShowGate(false);
            }
          } catch (parseErr) {
            console.warn('Corrupt cached user_data, clearing:', parseErr?.message);
            localStorage.removeItem('user_data');
            if (!cancelled) setShowGate(true);
          }
        } else {
          if (!cancelled) setShowGate(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setAuthReady(true);
        }
      }
    };

    initAuth();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    setShowGate(false);
    localStorage.setItem('user_data', JSON.stringify(userData));
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, phone = null) => {
    const response = await api.post('/auth/register', { email, password, name, phone });
    const { access_token, user: userData } = response.data;
    setToken(access_token);
    setUser(userData);
    setShowGate(false);
    localStorage.setItem('user_data', JSON.stringify(userData));
    return userData;
  }, []);

  // Called after lead-capture or Google OAuth success
  const setUserFromGate = useCallback((userData, accessToken) => {
    if (accessToken) setToken(accessToken);
    if (userData) {
      setUser(userData);
      localStorage.setItem('user_data', JSON.stringify(userData));
    }
    setShowGate(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await api.post('/auth/logout', {}, { headers });
    } catch (err) {
      // Best-effort: server may be unreachable. Local cleanup still runs.
      console.warn('Logout API call failed (proceeding with local cleanup):', err?.message);
    }
    setToken(null);
    setUser(null);
    setShowGate(true);
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_country_code');
  }, [token]);

  // Returns an auth header from in-memory token.
  // Even if the token is null the httpOnly cookie still
  // authenticates the request (api instance has withCredentials: true).
  const getAuthHeader = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  const isAuthenticated = !!user;

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    getAuthHeader,
    isAuthenticated,
    showGate,
    setShowGate,
    setUserFromGate,
    authReady,
  }), [user, token, loading, login, register, logout, getAuthHeader, isAuthenticated, showGate, setUserFromGate, authReady]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
